import cookie from 'js/lib/cookie';

const SSO_GRACE_PERIOD_COOKIE = 'ssoGracePeriodTimestamp';

export const GRACE_PERIOD_DURATION_MINUTES = 30;

/** Max orgs stored in the cookie to stay well within the ~4 KB browser limit. */
const MAX_ORG_ENTRIES = 20;

type GracePeriodMap = Record<string, number>;

/**
 * Safely parse the cookie value into a GracePeriodMap.
 * Returns an empty object for legacy (plain number), invalid JSON, or non-object values.
 */
const parseSSOGracePeriodCookie = (raw: string | null): GracePeriodMap => {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return parsed as GracePeriodMap;
  } catch {
    return {};
  }
};

/**
 * Evict the oldest entries when the map exceeds MAX_ORG_ENTRIES.
 */
const evictOldest = (map: GracePeriodMap): GracePeriodMap => {
  const keys = Object.keys(map);
  if (keys.length <= MAX_ORG_ENTRIES) return map;

  const sorted = keys.sort((a, b) => (map[a] ?? 0) - (map[b] ?? 0));
  const toRemove = sorted.slice(0, keys.length - MAX_ORG_ENTRIES);
  const result: GracePeriodMap = { ...map };
  toRemove.forEach((key) => delete result[key]);
  return result;
};

/**
 * Sets the SSO grace period cookie for a given organization.
 * Merges with existing entries and refreshes the cookie expiry.
 */
export const setSsoGracePeriodCookie = (orgId: string) => {
  if (!orgId) return;

  const map = parseSSOGracePeriodCookie(cookie.get(SSO_GRACE_PERIOD_COOKIE));
  map[orgId] = Date.now();
  const trimmed = evictOldest(map);

  cookie.set(SSO_GRACE_PERIOD_COOKIE, JSON.stringify(trimmed), {
    minutes: GRACE_PERIOD_DURATION_MINUTES,
    path: '/',
    secure: true,
    sameSite: 'Lax',
  });
};

/**
 * Checks if the given organization is within the SSO grace period.
 * @returns true if the org's timestamp exists and is within the grace period window
 */
export const isWithinSsoGracePeriod = (orgId: string): boolean => {
  if (!orgId) return false;

  const map = parseSSOGracePeriodCookie(cookie.get(SSO_GRACE_PERIOD_COOKIE));
  const timestamp = map[orgId];
  if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) return false;

  return Date.now() - timestamp < GRACE_PERIOD_DURATION_MINUTES * 60 * 1000;
};
